/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import React, { ReactNode } from 'react';
import { isMobile, Node, VariantType } from '@msdyn365-commerce-modules/utilities';

import { BusinessPartner, BusinessPartnerUser, BusinessPartnersDataActions } from '@msdyn365-commerce/retail-proxy';
import { IBusinessPartnersResources } from '../business-partners.props.autogenerated';
import { DataGrid, IDataGridColumn } from './data-grid';

/**
 * View model of business partner user, used to display.
 */
export interface IBusinessPartnerUserViewModel extends BusinessPartnerUser {
    name: string;
    spendingLimit: ReactNode;
}

/**
 * Props of BusinessPartnerUsersTable.
 */
export interface IBusinessPartnerUsersTableProps extends IComponentProps {
    className: string;
    pageSize?: number;
    businessPartner: BusinessPartner;
    resources: IBusinessPartnersResources;
    onSelect(businessPartnerUser: BusinessPartnerUser): void;
}

/**
 * BusinessPartnerUsersTable component.
 */
export class BusinessPartnerUsersTable extends React.Component<IBusinessPartnerUsersTableProps> {
    public shouldComponentUpdate(nextProps: Readonly<IBusinessPartnerUsersTableProps>): boolean {
        return this.props.id !== nextProps.id;
    }

    public render(): JSX.Element {
        return (
            <DataGrid
                {...this.props}
                loadData={this.getBusinessPartnerUsers}
                columns={this.getColumns()}
                defaultSorting={{ fieldName: 'name', ascending: true }}
                searchPlaceholderLabel={this.props.resources.businessPartnerSearchCustomer}
            />
        );
    }

    /**
     * Get column definition of the table.
     * @returns - Columns.
     */
    private readonly getColumns = (): IDataGridColumn<IBusinessPartnerUserViewModel>[] => {
        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';

        return [
            {
                reactKey: 'name',
                name: this.props.resources.businessUserTableNameHeadingText,
                isSearchable: true,
                fieldName: 'name',
                onClick: this.props.onSelect,
                onRender: (data: IBusinessPartnerUserViewModel) => <a role='button'>{data.name}</a>
            },
            {
                reactKey: 'email',
                name: this.props.resources.businessUserTableEmailHeadingText,
                fieldName: 'Email',
                onClick: this.props.onSelect
            },
            {
                reactKey: 'spendingLimit',
                name: this.props.resources.businessUserTableSpendingLimitHeadingText,
                className: `${this.props.className}__row__column-with-number`,
                fieldName: 'spendingLimit',
                onClick: !isMobileDevice ? this.props.onSelect : undefined
            }
        ];
    };

    /**
     * Call retail proxy to get business partner users.
     * @returns - Business partner users view model.
     */
    private readonly getBusinessPartnerUsers = async (): Promise<IBusinessPartnerUserViewModel[]> => {
        const businessPartners = await BusinessPartnersDataActions.getUsersAsync(
            {
                callerContext: this.props.context.actionContext,
                bypassCache: 'get',
                queryResultSettings: {}
            },
            this.props.businessPartner.BusinessPartnerId
        );
        return businessPartners
            .filter(user => user.StatusValue === 1)
            .map(user => {
                return {
                    ...user,
                    name: `${user.FirstName} ${user.LastName}`,
                    spendingLimit: (
                        <Node className='spending-limit'>
                            {!ObjectExtensions.isNullOrUndefined(user.SpendingLimit)
                                ? this.props.context.cultureFormatter.formatCurrency(user.SpendingLimit.toString())
                                : ''}
                            {!ObjectExtensions.isNullOrUndefined(user.SpendingLimit) && user.SpendingLimit === 0 ? (
                                <div
                                    className='msi-InfoCircle__tooltip'
                                    data-title={`${this.props.resources.businessUserTableSpendingLimitZeroHoverText}`}
                                >
                                    <i className='msi-InfoCircle' aria-hidden='true' />
                                </div>
                            ) : (
                                ''
                            )}
                        </Node>
                    )
                };
            });
    };
}
